import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";

import {
  FieldDefinitionType,
  FieldDefinitionTypeType,
} from "@/components/@types";
import { useResizeColumn } from "@/hooks/useResizeColumn";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import {
  useContextCellNavigation,
  useContextScope,
} from "@/hooks/useContextTree";
import { Values } from "@/constants";

import { useMotionValue, useTransform } from "framer-motion";
import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

// Import stores
import {
  useTreeStore,
  useTreeStoreApi,
  createTreeStore,
  TreeStoreContext,
  calculateVisibleIds,
} from "./Tree.store";

// Import components
import { GridAppearance } from "@/components/organisms/Table/Table.tools";
import { DocumentNode } from "graphql";
import { TreeHeader } from "./TreeHeader";
import { TreeLayout } from "./TreeLayout";

export type TreeDataType = {
  name?: string;
  children?: { id: string }[];
  [key: string]: any; // Additional dynamic properties
};

export type TreeQueryfetchingOptionsType = {
  type: "QUERY";
  query: DocumentNode;
  prepQuery: (id: string | null) => object;
  parseQuery: (data: any) => any;
};

export type TreeLocalfetchingOptionsType = {
  type: "LOCAL";
  getData: (id: string) => TreeDataType | null;
};

export type TreeDynamicfetchingOptionsType = {
  type: "DYNAMIC";
  getFetchData: (id: string) => TreefetchingOptionsType;
};

export type TreefetchingOptionsType =
  | TreeQueryfetchingOptionsType
  | TreeLocalfetchingOptionsType
  | TreeDynamicfetchingOptionsType;

export type TreeColumnType = {
  label: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  /** When "percentage", the column grows/shrinks with the container.
   *  The `width` value becomes the CSS min-width; resizing adjusts percentages. */
  widthMode?: "pixel" | "percentage";
  objectKey: string;
  isLockedLeft?: boolean;
  isLockedRight?: boolean;
  isResizable?: boolean;
  isEditable?: boolean;
  fieldType: FieldDefinitionTypeType;
  headerFieldType?: FieldDefinitionTypeType;
  renderHeader?: () => ReactNode;
  getValue: (data: any, index?: number) => any;
  getValueOptions?: FieldDefinitionType["getValueOptions"];
  /** Placeholder column while columns load; its cells render the field skeleton. */
  isLoading?: boolean;
  getCellMeta?: (data: any) => {
    isIrrelevant?: boolean;
  };
};

// Tree node type for internal tree structure
export type TreeNodeType = {
  id: string;
  parentId: string | null;
  isExpanded: boolean;
  childrenIds: string[];
  depth: number;
};

// Tree map type - a record of tree nodes indexed by their ID
export type TreeMapType = Record<string, TreeNodeType>;

export type TreeLayoutType = {
  verticalOverscan?: number;
  isSummaryRowVisible?: boolean;
  isCellBordersHidden?: boolean;
  hideOuterBorders?: boolean;
  isCheckboxEnabled?: boolean;
  isOpenDefaultDepth?: number;
  appearance?: GridAppearance;
  fetchingOptions: {
    row: TreefetchingOptionsType;
    column: TreefetchingOptionsType;
  };
  heads: TreeDataType[];
  editBranch?: (branchId: string, newBranchData: object) => void;
  deleteBranch?: (branchId: string) => void;
  moveBranch?: (branchId: string, newParentBranchId: string) => void;
  getChildrenIds?: (branch: TreeDataType) => string[];
  onRowClick?: (nodeId: string) => void;
  /** A cell is picked - by a click or the arrow keys - in the column named. */
  onCellFocus?: (nodeId: string, objectKey: string) => void;
  onVisibleRowCountChange?: (count: number) => void;
};

const COL_WIDTH = 150;
const COL_MAX_WIDTH = 800;

// Hook for handling column data fetching
function useColumnData(layout: TreeLayoutType) {
  const columnfetchingOptions = layout?.fetchingOptions?.column;

  let isLoading = false;
  let columns = [];

  if (columnfetchingOptions.type === "QUERY") {
    // For QUERY type column fetching
    const { loading: columnLoading, data: columnQueryData } = useQuery(
      columnfetchingOptions.query,
      {
        variables: columnfetchingOptions.prepQuery(null),
      },
    );

    isLoading = columnLoading;
    columns = columnfetchingOptions.parseQuery(columnQueryData);

    if (isLoading) {
      // Find locked left and right columns from the parsed columns
      const lockedLeft = columns.filter((col: any) => col.isLockedLeft);
      const lockedRight = columns.filter((col: any) => col.isLockedRight);

      // Generate loading columns for the unlocked section
      const loadingColumns = Array.from({ length: 32 }, (_, index) => ({
        label: `Loading ${index}`,
        objectKey: `loading-${index}`,
        isLockedLeft: false,
        isLockedRight: false,
        isResizable: false,
        fieldType: "currency",
        headerFieldType: "currency",
        isLoading: true,
        getValue: () => "",
        getValueOptions: () => ({}),
        width: 150,
        minWidth: 50,
        maxWidth: COL_MAX_WIDTH,
      }));

      // Compose columns: locked left + loading + locked right
      columns = [...lockedLeft, ...loadingColumns, ...lockedRight];
    }
  } else if (columnfetchingOptions.type === "LOCAL") {
    isLoading = false;
    columns = columnfetchingOptions.getData(null) as any[];
  }

  const memoizedColumns = useMemo(() => {
    return ((columns || []) as TreeColumnType[]).map((col) => ({
      id: col.objectKey || col.label.toLowerCase().replace(/\s+/g, "_"),
      objectKey: col.objectKey || col.label.toLowerCase().replace(/\s+/g, "_"),
      label: col.label,
      isLockedLeft: col.isLockedLeft || false,
      isLockedRight: col.isLockedRight || false,
      isEditable: col.isEditable || false,
      width: col.width || COL_WIDTH,
      minWidth: col.minWidth || 50,
      maxWidth: col.maxWidth || COL_MAX_WIDTH,
      widthMode: col.widthMode,
      isResizable: col.isResizable !== false,
      fieldType: col.fieldType,
      headerFieldType: col.headerFieldType,
      renderHeader: col.renderHeader,
      getValue: col.getValue,
      getValueOptions: col.getValueOptions,
      isLoading: col.isLoading,
      getCellMeta: col.getCellMeta,
    }));
  }, [columns.length]);

  return {
    columns: memoizedColumns,
    isLoading: isLoading,
  };
}

function TreeStoreProvider({ children }: { children: React.ReactNode }) {
  const storeRef = useRef<ReturnType<typeof createTreeStore> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createTreeStore();
  }
  return (
    <TreeStoreContext.Provider value={storeRef.current}>
      {children}
    </TreeStoreContext.Provider>
  );
}

export function Tree({ layout }: { layout: TreeLayoutType }) {
  return (
    <TreeStoreProvider>
      <TreeContent layout={layout} />
    </TreeStoreProvider>
  );
}

function TreeContent({ layout }: { layout: TreeLayoutType }) {
  const storeApi = useTreeStoreApi();

  // Initialize store
  const initialize = useTreeStore((state) => state.initialize);
  const setScrollPosition = useTreeStore((state) => state.setScrollPosition);
  const columnsLockedLeft = useTreeStore((state) => state.columnsLockedLeft);
  const columnsLockedRight = useTreeStore((state) => state.columnsLockedRight);
  const columnsUnlocked = useTreeStore((state) => state.columnsUnlocked);

  // Store refs for motion values
  const scrollX = useMotionValue(0);
  const scrollY = useMotionValue(0);
  const negativeScrollLeft = useTransform(scrollX, (value) => -value);

  // Calculate scroll offset for sticky rows based on target row
  const getScrollOffsetTop = useCallback(
    (targetRowIndex: number) => {
      const { treeMap, layout } = storeApi.getState();
      const visibleIds = calculateVisibleIds(treeMap, layout);

      if (targetRowIndex < 0 || targetRowIndex >= visibleIds.length) {
        return 0;
      }

      const targetNodeId = visibleIds[targetRowIndex];
      const targetNode = treeMap[targetNodeId];

      if (!targetNode) {
        return 0;
      }

      let ancestorCount = 0;
      let currentNode = targetNode;
      while (currentNode.parentId && treeMap[currentNode.parentId]) {
        ancestorCount++;
        currentNode = treeMap[currentNode.parentId];
      }

      return ancestorCount * Values.DEFAULT_ROW_HEIGHT;
    },
    [storeApi],
  );

  // Calculate scroll offset for locked left columns
  const getScrollOffsetLeft = useCallback(() => {
    let totalWidth = Values.DEFAULT_ROW_HEIGHT;
    for (const col of columnsLockedLeft) {
      totalWidth += col.width || COL_WIDTH;
    }
    return totalWidth;
  }, [columnsLockedLeft]);

  const { id: contextId, Scope, ref, node } = useContextScope();
  const { getClassName, getCellProps } = useContextCellNavigation(
    contextId,
    ref,
    node,
    {
      scrollOffsetTop: getScrollOffsetTop,
      scrollOffsetLeft: getScrollOffsetLeft,
      scrollBuffer: 1,
      tab: true,
    },
  );

  // Handle column data fetching for dynamic columns
  const { columns, isLoading } = useColumnData(layout);

  // Only subscribe to containerWidth when percentage columns need it for
  // pixel conversion. Pixel-only tables keep width in the store for
  // scroll virtualization (via get()) without re-rendering on panel resize.
  const needsContainerWidth = columns.some((c) => c.widthMode === "percentage");
  const containerWidth = useTreeStore((state) =>
    needsContainerWidth ? state.containerWidth : 0,
  );

  const onColumnResize = useCallback(
    (objectKey: string, left: number, width: number) => {
      storeApi.setState((state) => ({
        columnOffsets: {
          ...state.columnOffsets,
          [objectKey]: { left, width },
        },
      }));
    },
    [storeApi],
  );

  // Setup resize handler
  // initialOffset accounts for the chevron column (same width as row height)
  const { handleMouseDown } = useResizeColumn(columns, {
    minWidth: 50,
    maxWidth: COL_MAX_WIDTH,
    contextId,
    initialOffset: Values.DEFAULT_ROW_HEIGHT,
    ...(needsContainerWidth ? { containerWidth } : {}),
    onColumnResize,
  });

  // Initialize layout in store
  useEffect(() => {
    if (layout) {
      initialize(layout, contextId, handleMouseDown, columns);
    }
  }, [contextId, columns.length, layout]);

  useEffect(() => {
    return () => {
      storeApi.setState({
        contextId: "",
        treeMap: {},
        layout: null,
        scrollPosition: { x: 0, y: 0 },
        columnOffsets: {},
        containerWidth: 800,
        containerHeight: 800,
        visibleRowNodes: [],
        visibleStickyRowNodes: [],
        columnsLockedLeft: [],
        columnsLockedRight: [],
        columnsUnlocked: [],
        getChildrenIds: () => [],
        visibleStartColumnIndex: 0,
        visibleEndColumnIndex: 20,
        lastUpdateScrollX: 0,
      });
    };
  }, [storeApi]);

  // Track container dimensions for virtualization math (store get() only —
  // do not select these in React or panel resize will re-render the table).
  const containerRef = useResizeObserver((rect) => {
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    const prev = storeApi.getState();
    if (prev.containerWidth === width && prev.containerHeight === height) {
      return;
    }
    storeApi.setState({
      containerWidth: width,
      containerHeight: height,
    });
  });
  const subRootScrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (subRootScrollRef.current) {
      const { scrollLeft, scrollTop } = subRootScrollRef.current;
      scrollX.set(scrollLeft);
      scrollY.set(scrollTop);
      setScrollPosition(scrollLeft, scrollTop);
    }
  }, [scrollX, scrollY, setScrollPosition]);

  return (
    <Scope
      ref={containerRef}
      style={{
        width: "100%",
        minWidth: "100%",
        maxWidth: "100%",
        height: "100%",
        minHeight: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        position: "relative",
        paddingTop: Values.DEFAULT_ROW_HEIGHT,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onWheel={(e) => {
          if (subRootScrollRef.current) {
            subRootScrollRef.current.scrollLeft += e.deltaX || e.deltaY;
          }
        }}
      >
        <TreeHeader
          columnsLockedLeft={columnsLockedLeft}
          columnsUnlocked={columnsUnlocked}
          columnsLockedRight={columnsLockedRight}
          negativeScrollLeft={negativeScrollLeft}
          isLoading={isLoading}
        />
      </div>
      <TreeLayout
        contextId={contextId}
        scrollRef={subRootScrollRef}
        negativeScrollLeft={negativeScrollLeft}
        columnsLockedLeft={columnsLockedLeft}
        columnsUnlocked={columnsUnlocked}
        columnsLockedRight={columnsLockedRight}
        handleScroll={handleScroll}
        getCellProps={getCellProps}
        getClassName={getClassName}
      />
    </Scope>
  );
}

// Export the grid component as well for legacy usage
export default Tree;
